import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { clearLocalStorage } from "../js/localstorage";
import { useRouter } from "next/router";
import { loginState } from "../api/user/login-state";
import { getUserAccountInfo } from "../api/user/user-account-api";
import { getDistanceUnit } from "../service/location";
import { SystemError } from "../components/error/SystemError";
import { getRequestJSON } from "../api/index/location-api";
import { localiseRequestSettings } from "../utils/market-labels";
import { getMarketByName } from "../utils/markets";
import { setHistory } from "../service/history";
import { notificationHandle } from "./NotificationHandle";
import { peopleRequestHandle } from "./PeopleRequestHandle";
import { handleMyRequest } from "./MyRequestHandle";
import { HardLoadingCover } from "../components/commons/HardLoadingCover";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import { userStatusMap } from "../service/ws-service";
import { api_url } from "../utils/constants";
import { doc, serverTimestamp, setDoc } from "@firebase/firestore"
import { db } from "../firebase";
import { handleMyOrders } from "./MyOrdersHandle";
import { getMyCorpProfile } from "../api/corporate/corporate-api";

export const UserContext = React.createContext();

// Routes that authenticated users are allowed to stay on (no redirect to /home)
const AUTHENTICATED_ROUTES = [
    "/home", "/account", "/rewards", "/notification", "/profile",
    "/history", "/return-url-3ds", "/jobs", "/make-payment",
    "/booking", "/shared-job", "/team-job", "/terms", "/privacy-policy",
    "/sg/personal-trainer/personal-training-price-list", "/corporate",
];

// Routes that require login (redirect to /signin if not authenticated)
const LOGIN_REQUIRED_ROUTES = [
    "/home", "/account", "/notification", "/history",
    "/rewards", "/jobs", "/make-payment", "/booking",
];

export const UserProvider = ({ children, setIsUserLoggedIn, isUserLoggedIn, setIsShowFooter }) => {
    const router = useRouter();
    const route = router.route;
    
    const [ appType, setAppType ] = useState("web")
    const [ isCheckingLoginStatus, setIsCheckingLoginStatus ] = useState(true)
    const [ currentRoute, setCurrentRoute ] = useState(undefined);
    const [ userId, setUserId ] = useState(undefined);
    const [ userEmail, setUserEmail ] = useState(undefined);
    const [ userFullName, setUserFullName ] = useState(undefined);
    const [ userFirstName, setUserFirstName ] = useState(undefined);
    const [ userLastName, setUserLastName ] = useState(undefined);
    const [ userAccountType, setUserAccountType ] = useState(undefined);
    const [ userServiceCategory, setUserServiceCategory ] = useState(undefined);
    const [ userProfessionalType, setUserProfessionalType ] = useState(undefined);
    const [ userProfilePicture, setUserProfilePicture ] = useState(undefined);
    const [ userCountryName, setUserCountryName ] = useState("");
    const [ userCountryCd, setUserCountryCd ] = useState("");
    const [ userCountryCurrCd, setUserCountryCurrCd ] = useState(undefined);
    const [ userCountryTelCd, setUserCountryTelCd ] = useState(undefined);
    const [ userGender, setUserGender ] = useState(undefined);
    const [ userActiveInd, setUserActiveInd ] = useState(undefined);
    const [ isMobileVerified, setIsMobileVerified ] = useState("")
    const [ handleName, setHandleName ] = useState("");
    const [ userMobileNumber, setUserMobileNumber ] = useState(undefined);
    const [ hideMobileNumber, setHideMobileNumber ] = useState("N");
    const [ userLocation, setUserLocation ] = useState("");
    const [ userLatitude, setUserLatitude ] = useState(undefined);
    const [ userLongitude, setUserLongitude ] = useState(undefined);
    // Served city the backend derives from the coordinates (null = unknown).
    const [ userCity, setUserCity ] = useState(undefined);
    const [ userDescription, setUserDescription ] = useState(undefined);
    const [ userBirthDate, setUserBirthDate ] = useState(undefined);
    const [ userAddTimestamp, setUserAddTimestamp ] = useState(undefined);
    const [ userActiveTimestamp, setUserActiveTimestamp ] = useState(undefined);
    const [ userPrivacySetting, setUserPrivacySetting ] = useState(undefined);
    const [ appNotification, setAppNotification ] = useState(undefined);
    const [ emailNotification, setEmailNotification ] = useState(undefined);
    const [ userCommissionFeePercent, setUserCommissionFeePercent ] = useState("")
    const [ userPerJobPay, setUserPerJobPay ] = useState("")
    const [ penaltyLevel, setPenantlyLevel ] = useState("")
    const [ isOnboarded, setIsOnboarded ] = useState(false);
    
    const [ requestCategorySettings, setRequestCategorySettings ] = useState([]);
    const [ radius, setRadius ] = useState(50);
    const [ chipsBalance, setChipsBalance ] = useState(0);
    const [ spSubscriptions, setSpSubscriptions ] = useState({});
    const [ businessType, setBusinessType ] = useState("")
    const [ businessName, setBusinessName ] = useState("")

    //Notification*************************
    const [ isLoadingNotifications, setIsLoadingNotifications ] = useState(false);
    const [ notificationCount, setNotificationCount ] = useState(0);
    const [ notificationArray, setNotificationArray ] = useState([]);
    const [ notificationIncrementSize, setNotificationIncrementSize ] = useState(15);
    const [ notificationLowerLimit, setNotificationLowerLimit ] = useState(0);
    const [ isShowMoreNotification, setIsShowMoreNotification ] = useState(true);
    const [ notificationPermission, setNotificationPermission ] = useState("")

    //My Requests************************************
    const requestArrayLength = useRef(0)
    const [ isRefreshRequest, setIsRefreshRequest ] = useState(false);
    const [ isLoadingMyRequests, setIsLoadingMyRequests ] = useState(false);
    const [ requestArray, setRequestArray ] = useState([])
    const [ requestIncrementSize, setRequestIncrementSize ] = useState(10);
    const [ pageNumber, setPageNumber ] = useState(0);
    const [ isShowMoreRequest, setIsShowMoreRequest ] = useState(true);
    const [ isNoRequest, setIsNoRequest ] = useState(false)

    //My Orders********************************    
    const [ orderIncrementSize, setOrderIncrementSize ] = useState(20);
    const [ orderOldIncrementSize, setOrderOldIncrementSize ] = useState(6);
    const [ orderPageNumber, setOrderPageNumber ] = useState(0);
    const [ orderOldPageNumber, setOrderOldPageNumber ] = useState(0);
    const [ isShowMoreOldRequest, setIsShowMoreOldRequest ] = useState(true);
    const [ isShowMoreOngoingRequest, setIsShowMoreOngoingRequest ] = useState(true);
    const [ isRefreshOrders, setIsRefreshOrders ] = useState(false);
    const [ isLoadingMyOrders, setIsLoadingMyOrders ] = useState(false);
    const [ isLoadingMyOldOrders, setIsLoadingMyOldOrders ] = useState(false);
    const [ ongoingRequestArray, setOngoingRequestArray ] = useState([])
    const [ requestHistoryArray, setRequestHistoryArray ] = useState([])    

    //People Request**************************
    const [ isPeopleRequestRefresh, setIsPeopleRequestRefresh ] = useState(false);
    const [ isLoadingPeopleRequests, setIsLoadingPeopleRequests ] = useState(false);
    const [ requestPeopleArray, setRequestPeopleArray ] = useState([]); 
    const [ peopleRequestIncrementSize, setPeopleRequestIncrementSize ] = useState(10);
    const [ peopleRequestLowerLimit, setPeopleRequestLowerLimit ] = useState(0);
    const [ isShowMorePeopleRequest, setIsShowMorePeopleRequest ] = useState(true);
    const [ isNoPeopleRequest, setIsNoPeopleRequest ] = useState(false)

    //Buzz **************************
    const [ isBuzzRefresh, setIsBuzzRefresh ] = useState(false);
    const [ buzzArray, setBuzzArray ] = useState([])
    const [ buzzLowerLimit , setBuzzLowerLimit ] = useState(0)
    const [ buzzIncrementSize, setBuzzIncrementSize ] = useState(10)
    const [ isShowMoreBuzz, setIsShowMoreBuzz ] = useState(true)
    const [ isLoadingBuzz, setIsLoadingBuzz ] = useState(false)

    //FitnessDeals**********
    const [ fitnessDealsLowerLimit , setFitnessDealsLowerLimit ] = useState(0)
    const [ fitnessDealsArray, setFitnessDealsArray ] = useState([])

    //Corporate Profile**********
    const [ corporateProfile, setCorporateProfile ] = useState(null);

    const [ isLoading, setIsLoading ] = useState(false);
    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}); 

    const distanceUnit = getDistanceUnit(userCountryName);
    
    ///MY REQUESTS ******************************************
    const { refreshRequest, getMyRequests } 
    = handleMyRequest(
        userId, 
        pageNumber, 
        requestArray, 
        setRequestArray, 
        requestIncrementSize, 
        isNoRequest,
        setIsNoRequest,
        setIsLoadingMyRequests,
        setIsShowMoreRequest, 
        setErrorContent, 
        setIsSystemError, 
        setPageNumber, 
        requestArrayLength, 
        setIsRefreshRequest, 
        isRefreshRequest, 
        userAccountType, 
        userServiceCategory,
        );

    ///MY ORDERS ******************************************
    const { refreshOrders, getOngoingRequests, getOldRequests } 
    = handleMyOrders(
        orderIncrementSize,
        orderOldIncrementSize,
        orderPageNumber,
        setOrderPageNumber,  
        orderOldPageNumber,
        setOrderOldPageNumber,
        ongoingRequestArray,
        setOngoingRequestArray,
        requestHistoryArray,
        setRequestHistoryArray,
        isLoadingMyOrders,
        setIsLoadingMyOrders,
        isLoadingMyOldOrders,
        setIsLoadingMyOldOrders,
        isShowMoreOngoingRequest,
        setIsShowMoreOngoingRequest,
        isShowMoreOldRequest,
        setIsShowMoreOldRequest,
        setErrorContent, 
        setIsSystemError, 
        isRefreshOrders, 
        setIsRefreshOrders, 
        userAccountType, 
    );

    ///PEOPLE REQUESTS ******************************************
    const { refreshPeopleRequest, getPeopleRequests } 
    = peopleRequestHandle(
        userId, 
        userServiceCategory, 
        peopleRequestIncrementSize, 
        peopleRequestLowerLimit, 
        userCountryName, 
        userLatitude, 
        userLongitude, 
        distanceUnit, 
        radius, 
        isNoPeopleRequest,
        setIsNoPeopleRequest,
        setIsLoadingPeopleRequests, 
        requestPeopleArray, 
        setRequestPeopleArray, 
        setIsShowMorePeopleRequest, 
        setErrorContent, 
        setIsSystemError, 
        setPeopleRequestLowerLimit, 
        setIsPeopleRequestRefresh, 
        userAccountType, 
        isPeopleRequestRefresh);

    //NOTIFICATION HANDLE +++++++++++++++++++++++++++++++++    
    const { refreshNotification, notificationSeen, getNotifications } 
        = notificationHandle(
            setNotificationCount, 
            userId, setErrorContent,
             setIsSystemError, 
             notificationArray, 
             setNotificationArray, 
             refreshRequest, 
             refreshOrders,
             refreshPeopleRequest, 
             notificationIncrementSize, 
             notificationLowerLimit, 
             setIsLoadingNotifications, 
             setIsShowMoreNotification, 
             setNotificationLowerLimit, 
             notificationCount,
             notificationPermission, 
             setNotificationPermission);

    // ==================== Core User Functions ====================

    const getUserAccount = useCallback(async () => {
        try {
            let userAccountResponse = await getUserAccountInfo();
            setUserEmail(userAccountResponse.loginUserId);
            setUserFirstName(userAccountResponse.userFirstName);
            setUserLastName(userAccountResponse.userLastName);
            setUserAccountType(userAccountResponse.userAccountType);
            setUserServiceCategory(userAccountResponse.category);
            setUserProfessionalType(userAccountResponse.professionalType)
            setUserCountryName(userAccountResponse.countryName);
            setUserCountryCd(userAccountResponse.countryCd);
            setUserCountryCurrCd(userAccountResponse.countryCurrCd);
            setUserCountryTelCd(userAccountResponse.countryTelCd);
            setUserGender(userAccountResponse.gender);
            setUserActiveInd(userAccountResponse.userActiveInd);
            setIsMobileVerified(userAccountResponse.isMobileVerified);
            setHandleName(userAccountResponse.handleName);
            setUserMobileNumber(userAccountResponse.mobileNumber);
            setUserLocation(userAccountResponse.userLocation);        
            setUserLatitude(userAccountResponse.userLatitude);
            setUserLongitude(userAccountResponse.userLongitude);
            setUserCity(userAccountResponse.city);
            setUserProfilePicture(userAccountResponse.profilePic);
            setHideMobileNumber(userAccountResponse.hideMobileNumber);
            setUserDescription(userAccountResponse.description);
            setUserBirthDate(userAccountResponse.birthDate);
            setUserAddTimestamp(userAccountResponse.userAddTimetamp);
            setUserActiveTimestamp(userAccountResponse.userActiveTimestamp);
            setUserPrivacySetting(userAccountResponse.userPrivacySetting);

            // Corporate profile: fetch if user is a corporate employee
            if(userAccountResponse.isCorporateUser){
                try {
                    const corpProfile = await getMyCorpProfile();
                    if(corpProfile && corpProfile.success){
                        setCorporateProfile(corpProfile);
                    }
                } catch (corpError) {
                    console.log("Could not load corporate profile:", corpError);
                }
            }

            if(userAccountResponse.userAccountType === "Professional"){
                setAppNotification(userAccountResponse.appNotification);
                setEmailNotification(userAccountResponse.emailNotification);
                setUserCommissionFeePercent(userAccountResponse.commissionFeePercent)
                setUserPerJobPay(userAccountResponse.perJobPay)
                setPenantlyLevel(userAccountResponse.penaltyLevel)
                setBusinessName(userAccountResponse.businessName)
                setIsOnboarded(userAccountResponse.isOnboarded)
            }
        } catch (error) {
            console.log(error);
            setErrorContent({'header': "Error fetching User Account", 'body': "Please reload the app and try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
        }
    }, [])

    const getUserState = useCallback(async () => {
        try{
            setIsCheckingLoginStatus(true)
            let loginStateResponse = await loginState(); 
            if(loginStateResponse.message === "loggedIn"){  
                setIsShowFooter(false)
                if(!isUserLoggedIn){
                    setIsUserLoggedIn(true)            
                }  
                if(userId != loginStateResponse.id){
                    setUserId(loginStateResponse.id.toString());
                    setUserFullName(loginStateResponse.name);
                    getUserAccount();

                    try { 
                        await setDoc(doc(db, "appNotification", loginStateResponse.id.toString()), {
                            type: "login",
                            lastMessageTime: serverTimestamp()
                        }, { merge: true });
                    } catch (error) {
                        console.log(error)
                    }
                }               
                
                const isOnAuthenticatedRoute = AUTHENTICATED_ROUTES.some(r => route.includes(r));
                if(!isOnAuthenticatedRoute){
                    setIsLoading(true)
                    router.push("/home").then(() => setIsLoading(false));                                 
                }   
                setIsCheckingLoginStatus(false)             
            } else {
                setIsShowFooter(true)
                if(isUserLoggedIn){
                    setIsUserLoggedIn(false)            
                }  
                clearLocalStorage();

                const needsLogin = LOGIN_REQUIRED_ROUTES.some(r => route.includes(r));
                if(needsLogin && !route.includes("/home/buzz/")){
                    setIsLoading(true)
                    router.push(`/signin?redirect=${route}`).then(() => setIsLoading(false));                 
                }
                setIsCheckingLoginStatus(false)
            }           
        } catch (error){
            console.log(error);
            setErrorContent({'header': "Login state expired", 'body': "For security reasons you need to re-login."});
            setIsSystemError(true);
            window.localStorage.clear();
            const redirectPath = appType !== "web" ? "/app" : "/signin";
            router.push(redirectPath).then(() => setIsLoading(false));
        }
    }, [route, userId, isUserLoggedIn, appType, setIsShowFooter, setIsUserLoggedIn, getUserAccount, router])
    
    // ==================== Route Change Detection ====================

    useEffect(() => {
        if(currentRoute !== route){
            setHistory(route);
            setCurrentRoute(route)
            getUserState()
        } else if(!currentRoute){
            getUserState()
        }         
    }, [route, currentRoute, getUserState])

    // ==================== Request Category Settings ====================

    useEffect(() => {        
        if (!userCountryName) return;
        const loadSettings = async () => {
            try {
                // UAE / Malaysia: local question wording and choice labels (keys unchanged).
                setRequestCategorySettings(localiseRequestSettings(await getRequestJSON(userCountryName), getMarketByName(userCountryName)));
            } catch (error){
                console.log(error);
                setErrorContent({'header': "System Error", 'body': "Cannot get service list for your location. Please reload the app. If problem persists, contact us at support@stringssg.com"});
                setIsSystemError(true);
            }
        };
        loadSettings();
    }, [userCountryName])

    useEffect(() => {
        if(requestCategorySettings.length === 0 || !userServiceCategory) return;
        
        for(let x in requestCategorySettings){
            if (requestCategorySettings[x].requestType === userServiceCategory){
                setBusinessType(requestCategorySettings[x].businessType)           
            }
        }; 
    }, [requestCategorySettings, userServiceCategory, setBusinessType])

    // ==================== App Type Detection ====================
    
    useEffect(() => {
        const ua = navigator.userAgent;
        
        // Check for your custom app tag first, then OS
        const isStringsApp = /StringsSG\/[0-9\.]+/.test(ua);

        if (isStringsApp) {
            if (/Android/i.test(ua)) {
                setAppType("android");
            } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
                setAppType("ios");
            }
        }
    }, []);

    useEffect(() => {
        // "/" is the global page and "/sg" the Singapore home - neither is
        // for the app's webview, which has its own landing at /app.
        if(router.pathname !== "/" && router.pathname !== "/sg") return
        if (appType !== "web"){
            setIsLoading(true)
            router.push("/app").then(() => setIsLoading(false))
        }
    }, [appType, router])

    // ==================== WEB SOCKET HANDLING ====================

    const [ wsStatus, setWsStatus ] = useState("");
    const [ stompClient, setStompClient ] = useState("");
    const [ isWsConnected, setIsWsConnected ] = useState(false)

    const sendWsMessage = useCallback((status, recipient, messageType) => {
        stompClient.send("/sendMessage", {}, JSON.stringify({
            "userId" : userId,
            "status" : status,
            "recipient" : recipient,
            "messageType" : messageType
        }));
    }, [stompClient, userId])

    const sendWsMessageToServiceProvider = useCallback((status, recipient, messageType, serviceCategory) => {       
        stompClient.send("/service-provider/"+serviceCategory, {}, JSON.stringify({
            "userId" : userId,
            "status" : status,
            "recipient" : recipient,
            "messageType" : messageType
        }));
    }, [stompClient, userId])

    const sendWsPrivateMessage = useCallback((status, recipient, messageType) => {
        stompClient.send("/sendPrivateMessage", {}, JSON.stringify({
            "userId" : userId,
            "status" : status,
            "recipient" : recipient,
            "messageType" : messageType
        }));
    }, [stompClient, userId])

    const handleIncomingMessage = useCallback((messageObj) => {
        if(messageObj.status === "online" && messageObj.messageType === "loggedIn"){
            if(messageObj.userId != userId){
                sendWsPrivateMessage("online", messageObj.userId, "userStatus")
            }
        }

        if(messageObj.messageType === "loggedIn" || messageObj.messageType === "userStatus"){
            userStatusMap.set(messageObj.userId, messageObj.status);
        }
    }, [userId, sendWsPrivateMessage])
    
    useEffect(() => {
        if(!userId) return
        getOngoingRequests()
    }, [userId, getOngoingRequests])
    
    // User activity / idle detection              
    const activity = useCallback(() => {
        let actSetT;
        if(actSetT){
            clearTimeout(actSetT)
        }
        actSetT = setTimeout(() => {
            if(userStatusMap.get(userId) === "idle") return
            sendWsMessage("idle", "all", "userStatus")
        }, 300000)

        if(userStatusMap.get(userId) === "idle"){
            sendWsMessage("online", "all", "userStatus")
        }

    }, [sendWsMessage, userId])
    
    const activityEvents = useRef(['touchstart']);

    const onMessageReceived = useCallback((msg) => {
        if (msg.body) {
            var jsonBody = JSON.parse(msg.body);
            if (jsonBody) {
                setWsStatus(jsonBody)
                handleIncomingMessage(jsonBody)
            }
        }
    }, [handleIncomingMessage])

    useEffect(() => {
        if(!stompClient || !userId) return
        
        stompClient.debug = null

        const onConnected = () => {
            setIsWsConnected(true)
            stompClient.subscribe('/topic/message', onMessageReceived); 
            stompClient.subscribe('/user/'+userId+'/private', onMessageReceived)

            if(userServiceCategory){
                stompClient.subscribe('/topic/service-provider/' + userServiceCategory, onMessageReceived)
            }

            sendWsMessage("online", "all", "loggedIn")       
            
            activityEvents.current.forEach(function(eventName) {
                document.addEventListener(eventName, activity, true);
            }); 
        }

        const disconnect = () => {
            if (stompClient !== null) {
                stompClient.disconnect();
                setIsWsConnected(false)
            }
        }
    
        const onError = () => {
            disconnect();
        }

        stompClient.connect({}, onConnected, onError);

    }, [stompClient, userId, userServiceCategory, onMessageReceived, sendWsMessage, activity])
    
    useEffect(() => {
        if(!userId || !isUserLoggedIn) return
        
        let homeLink = api_url
        
        if(document.location.host !== "localhost:3000"){
            homeLink = api_url + ":8443"
        }

        let Sock = new SockJS(homeLink + "/websocket")
        setStompClient(Stomp.over(Sock))

    }, [userId, isUserLoggedIn])

    // Remove old activity listeners on cleanup
    useEffect(() => {
        activityEvents.current.forEach(function(eventName) {
            document.removeEventListener(eventName, activity, true);
        }); 
    }, [activity])

    // ==================== Memoized Context Value ====================

    const contextValue = useMemo(() => ({
        //User Settings***********
        appType,
        isCheckingLoginStatus,
        getUserAccount,
        userId,
        setUserId,
        userEmail,
        userFullName,
        userFirstName,
        setUserFirstName,
        userLastName,
        setUserLastName,
        userGender, 
        setUserGender,
        userActiveInd,
        setUserActiveInd,
        isMobileVerified,
        setIsMobileVerified,
        handleName,
        setHandleName,
        userDescription,
        setUserDescription,
        userBirthDate,
        setUserBirthDate,
        userPrivacySetting, 
        setUserPrivacySetting,
        userAccountType,
        setUserAccountType,
        userServiceCategory,
        setUserServiceCategory,
        userProfessionalType,
        setUserProfessionalType,
        userLocation,
        setUserLocation,
        userCountryName,
        setUserCountryName,
        userCountryCd,
        setUserCountryCd,
        userCountryCurrCd,
        setUserCountryCurrCd,
        userCountryTelCd,
        setUserCountryTelCd,
        userMobileNumber,
        setUserMobileNumber,
        hideMobileNumber,
        setHideMobileNumber,
        userProfilePicture,
        setUserProfilePicture,
        userLatitude,
        setUserLatitude,
        userLongitude,
        setUserLongitude,
        userCity,
        userAddTimestamp,
        userCommissionFeePercent,    
        userPerJobPay,   
        penaltyLevel,
        distanceUnit,   
        businessName,
        isOnboarded,                
        requestCategorySettings,
        //Sp Subscription
        chipsBalance,
        setChipsBalance,
        spSubscriptions,
        setSpSubscriptions,
        businessType,
        setBusinessType,
        //Notification*****************
        refreshNotification,
        notificationSeen,
        isLoadingNotifications,
        setIsLoadingNotifications,
        notificationCount,
        setNotificationCount,
        notificationArray,
        setNotificationArray,
        getNotifications,
        notificationIncrementSize,
        setNotificationIncrementSize,
        notificationLowerLimit,
        setNotificationLowerLimit,
        isShowMoreNotification,
        setIsShowMoreNotification,
        //People Request ******************
        isLoadingPeopleRequests,
        setIsLoadingPeopleRequests,
        requestPeopleArray,
        setRequestPeopleArray,
        peopleRequestLowerLimit,
        setPeopleRequestLowerLimit,
        peopleRequestIncrementSize,
        setPeopleRequestIncrementSize,
        isShowMorePeopleRequest,
        setIsShowMorePeopleRequest,
        getPeopleRequests,
        refreshPeopleRequest,
        radius,
        setRadius,
        isNoPeopleRequest,
        setIsNoPeopleRequest,
        //My Requests *******
        isLoadingMyRequests,
        setIsLoadingMyRequests,
        getMyRequests,
        refreshRequest,
        requestArrayLength,
        requestArray,
        setRequestArray,
        requestIncrementSize, 
        setRequestIncrementSize,
        pageNumber, 
        setPageNumber,
        isShowMoreRequest, 
        setIsShowMoreRequest,
        isNoRequest,
        setIsNoRequest,
        //My Orders *******
        isLoadingMyOrders,
        setIsLoadingMyOrders,
        isLoadingMyOldOrders, 
        setIsLoadingMyOldOrders,
        getOngoingRequests,
        getOldRequests,
        refreshOrders,
        ongoingRequestArray,
        setOngoingRequestArray,
        requestHistoryArray,
        setRequestHistoryArray,
        orderIncrementSize, 
        setOrderIncrementSize,
        orderOldIncrementSize,
        setOrderOldIncrementSize,
        orderPageNumber, 
        setOrderPageNumber,
        orderOldPageNumber,
        setOrderOldPageNumber,
        isShowMoreOngoingRequest,
        isShowMoreOldRequest, 
        setIsShowMoreOldRequest,
        //Buzz ******
        buzzArray,
        setBuzzArray,
        buzzLowerLimit,
        setBuzzLowerLimit,
        buzzIncrementSize,
        isShowMoreBuzz, 
        setIsShowMoreBuzz,
        isLoadingBuzz,
        setIsLoadingBuzz,
        isBuzzRefresh, 
        setIsBuzzRefresh,
        //Fitness Deals *****
        fitnessDealsLowerLimit,
        setFitnessDealsLowerLimit,
        fitnessDealsArray,
        setFitnessDealsArray,
        //Corporate Profile *****
        corporateProfile,
        setCorporateProfile,
        //WEB SOCKET
        wsStatus, 
        setWsStatus,
        stompClient,
        isWsConnected,
        sendWsMessage,
        sendWsMessageToServiceProvider,
        sendWsPrivateMessage
    }), [
        appType, 
        isCheckingLoginStatus, 
        getUserAccount, 
        userId, 
        userEmail, 
        userFullName,
        userFirstName, 
        userLastName, 
        userGender, 
        userActiveInd, 
        isMobileVerified, 
        handleName,
        userDescription, 
        userBirthDate, 
        userPrivacySetting, 
        userAccountType, 
        userServiceCategory,
        userProfessionalType, 
        userLocation, 
        userCountryName, 
        userCountryCd, 
        userCountryCurrCd,
        userCountryTelCd, 
        userMobileNumber, 
        hideMobileNumber, 
        userProfilePicture, 
        userLatitude,
        userLongitude, 
        userCity,
        userAddTimestamp, 
        userCommissionFeePercent, 
        userPerJobPay,
        penaltyLevel, 
        distanceUnit,
        businessName, 
        isOnboarded,
        requestCategorySettings, 
        chipsBalance, 
        spSubscriptions,
        businessType, 
        refreshNotification, 
        notificationSeen, 
        isLoadingNotifications, 
        notificationCount,
        notificationArray, 
        getNotifications, 
        notificationIncrementSize, 
        notificationLowerLimit,
        isShowMoreNotification, 
        isLoadingPeopleRequests, 
        requestPeopleArray, 
        peopleRequestLowerLimit,
        peopleRequestIncrementSize, 
        isShowMorePeopleRequest, 
        getPeopleRequests, 
        refreshPeopleRequest,
        radius, 
        isNoPeopleRequest, 
        isLoadingMyRequests, 
        getMyRequests, 
        refreshRequest, 
        requestArrayLength,
        requestArray, 
        requestIncrementSize, 
        pageNumber, 
        isShowMoreRequest, 
        isNoRequest, 
        isLoadingMyOrders,
        isLoadingMyOldOrders, 
        getOngoingRequests, 
        getOldRequests, 
        refreshOrders, 
        ongoingRequestArray,
        requestHistoryArray, 
        orderIncrementSize, 
        orderOldIncrementSize, 
        orderPageNumber, 
        orderOldPageNumber,
        isShowMoreOngoingRequest, 
        isShowMoreOldRequest, 
        buzzArray, 
        buzzLowerLimit, 
        buzzIncrementSize,
        isShowMoreBuzz, 
        isLoadingBuzz, 
        isBuzzRefresh, 
        fitnessDealsLowerLimit, 
        fitnessDealsArray,
        corporateProfile, 
        wsStatus, 
        stompClient, 
        isWsConnected, 
        sendWsMessage,
        sendWsMessageToServiceProvider, 
        sendWsPrivateMessage
    ]);

    return (
    <>
    {isLoading && <HardLoadingCover/>}
    <UserContext.Provider value={contextValue}>
        {children}        
        {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
    </UserContext.Provider>
    </>
    )
}
